import { Component, computed, DestroyRef, ElementRef, inject, NgZone, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { RangeEventArgs } from '@syncfusion/ej2-angular-calendars';
import { CheckBoxSelectionService } from '@syncfusion/ej2-angular-dropdowns';
import { SelectEventArgs } from '@syncfusion/ej2-angular-navigations';
import { TooltipComponent, TooltipEventArgs } from '@syncfusion/ej2-angular-popups';
import { EMPTY, forkJoin } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppPath } from 'src/app/app-path';
import { BreadcrumbsService } from 'src/app/common/breadcrumbs/breadcrumbs.service';
import { FonctionLogicielle } from 'src/app/constantes/fonctions-logicielle-constantes';
import { BpfAuditGlobal, BpfAuditNavigation, BpfAuditSession, CategorieAuditBpf } from 'src/app/models/bpf/bpf-audit.model';
import {
  BpfDetail,
  BpfBlocF3,
  BpfBlocFormateurs,
  BpfDetailFormateur,
  BpfDetailNavigation,
  BpfDetailNavigationType,
  BpfDetailValeur,
  BpfEffectifFormateurs,
  BpfFinancementDetail,
  BpfInscriptionDetail,
  BpfLigneFinanciere,
  BpfRepartition,
  BpfResume,
  BpfTotalCadre,
  BpfTotalStagiaires,
} from 'src/app/models/bpf/bpf-resume.model';
import { EntiteModel } from 'src/app/models/entites/entite.model';
import { OrganismeFormationModel } from 'src/app/models/organisme-formation/OrganismeFormationModel';
import { ToastErrorModel } from 'src/app/models/toasts/toast-error.model';
import { NavigationService } from 'src/app/navigation/services/navigation.service';
import { BpfAuditService } from 'src/app/services/bpf/bpf-audit.service';
import { BpfEcranStateService } from 'src/app/services/bpf/bpf-ecran-state.service';
import { BpfResumeService } from 'src/app/services/bpf/bpf-resume.service';
import { EntitesService } from 'src/app/services/entites/entites.service';
import { ModalService } from 'src/app/services/modal/modal-service';
import { NotificationToastService } from 'src/app/services/notifications/notification-toast.service';
import { OrganismeFormationService } from 'src/app/services/organisme-formation/organisme-formation.service';
import { UtilisateurCourantService } from 'src/app/services/utilisateurs/utilisateur-courant.service';
import { ValeurMonetaireFrPipe } from 'src/app/utils/pipes/valeur-monetaire.pipe';
import { StatistiquesPath } from '../statistiques/statistiques-path';

export const BPF_DETAIL_MODAL_TYPE = 'bpf-detail';

/** Clé renvoyée par le back pour la ligne "Non renseigné" de F-3/F-4 (inscriptions sans niveau ou sans spécialité BPF). */
export const CLE_NON_RENSEIGNE = -1;

/** Barres de répartition de la synthèse pédagogique : une couleur par ligne CERFA (palette de la synthèse financière + $courrier-color, $purple). */
const COULEURS_REPARTITION = ['#2e7cff', '#3acd85', '#ffb11b', '#ec087e', '#2aa9ae', '#d67df4'];
const COULEUR_NON_RENSEIGNE = '#a7adb5';
/** Barres en deux parts de la synthèse pédagogique : interne ($color-main) / externe ($orange). */
const COULEUR_INTERNE = '#2e7cff';
const COULEUR_EXTERNE = '#ff890f';

/** Valeur du filtre gestionnaire pour les sessions sans gestionnaire administratif (ni sur la session, ni sur sa formation). */
export const SANS_GESTIONNAIRE = -1;

export interface GestionnaireAudit {
  id: number;
  libelle: string;
  nombreSessions: number;
}

/** Un segment de la synthèse visuelle (donut du bilan financier) : regroupement de lignes CERFA par grande famille de financeurs. */
interface SegmentSynthese {
  label: string;
  value: number;
  couleur: string;
  pct: number;
  /** Ligne agrégée (mêmes financements sous-jacents que les lignes CERFA regroupées) pour rouvrir la modale de détail déjà utilisée par le cadre C. */
  ligne: BpfLigneFinanciere;
}

/** Un segment de la barre empilée de la carte Fiabilité (une catégorie d'audit). */
interface SegmentFiabilite {
  label: string;
  count: number;
  pct: number;
  couleur: string;
}

/** Une part d'une barre empilée de la synthèse pédagogique. `pct` : part des heures (barres en deux parts) ou des stagiaires (répartitions). */
export interface PartSynthesePedagogique {
  libelle: string;
  nombre: number;
  /** Unité abrégée ('form.' ou 'stag.'), comme sur la maquette, pour laisser la place au libellé. */
  unite: string;
  heures: number;
  pct: number;
  couleur: string;
  /** Ouvre la modale de détail ; absent pour une part sans détail (ex. activité propre de l'organisme). */
  detail?: () => void;
}

export interface ChiffreCleSynthese {
  valeur: number;
  suffixe: string;
  libelle: string;
}

/** Un cadre de la synthèse pédagogique (E, F ou G) : chiffres clés + barre empilée. */
export interface PanneauSynthesePedagogique {
  cadre: string;
  titre: string;
  chiffres: ChiffreCleSynthese[];
  /** Légende de la barre (heures ou stagiaires), pour lever l'ambiguïté du pourcentage. */
  baseDuPourcentage: string;
  parts: PartSynthesePedagogique[];
}

/** Une ligne du cadre A. `valeur` nulle : non renseignée dans le paramétrage de l'organisme, sauf si `indisponible` (information qu'EParcours ne stocke pas du tout, à saisir directement sur le CERFA). */
interface LigneIdentification {
  libelle: string;
  valeur: string | null;
  indisponible?: boolean;
}

/** Le cadre A vient d'EntitesService, indépendamment des routes BPF : son échec ne doit pas bloquer les autres cadres. */
type EtatOrganisme = 'chargement' | 'charge' | 'erreur';

@Component({
  selector: 'app-export-bpf',
  templateUrl: './export-bpf.component.html',
  // Mode cases à cocher du sélecteur multi-entités.
  providers: [CheckBoxSelectionService],
  styleUrls: ['./export-bpf.component.scss']
})
export class ExportBpfComponent implements OnInit {
  public readonly modalType = BPF_DETAIL_MODAL_TYPE;
  public readonly annees: number[] = [2024, 2025, 2026, 2027];

  public entites: EntiteModel[] = [];
  public idsEntiteSelectionnees: number[] = [];
  public anneeSelectionnee = new Date().getFullYear();
  public dateDebut = this.calculerDateDebutParDefaut(this.anneeSelectionnee);
  public dateFin = this.calculerDateFinParDefaut(this.anneeSelectionnee);

  public readonly TAILLE_PAGE_AUDIT = 10;

  public resume = signal<BpfResume | null>(null);
  public auditGlobal = signal<BpfAuditGlobal | null>(null);
  /** Bloquante/Alerte/Exclue sélectionnées par défaut (Conforme n'a jamais rien à corriger). */
  private readonly CATEGORIES_PAR_DEFAUT = new Set<CategorieAuditBpf>([
    CategorieAuditBpf.Bloquante,
    CategorieAuditBpf.Alerte,
    CategorieAuditBpf.Exclue,
  ]);
  public categoriesSelectionnees = signal<Set<CategorieAuditBpf>>(new Set(this.CATEGORIES_PAR_DEFAUT));
  public motifSelectionne = signal<string | null>(null);
  /** null = tous les gestionnaires ; SANS_GESTIONNAIRE = sessions sans gestionnaire administratif. */
  public gestionnaireSelectionne = signal<number | null>(null);
  /** Audit restreint au gestionnaire choisi : sessions ET compteurs (donc jauge de fiabilité et motifs) en tiennent compte. */
  public auditAffiche = computed<BpfAuditGlobal | null>(() => {
    const audit = this.auditGlobal();
    const idGestionnaire = this.gestionnaireSelectionne();
    if (!audit || idGestionnaire === null) {
      return audit;
    }
    const sessions = audit.sessions.filter((session) => (session.idGestionnaireAdministratif ?? SANS_GESTIONNAIRE) === idGestionnaire);
    const compter = (categorie: CategorieAuditBpf) => sessions.filter((session) => session.categorie === categorie).length;
    return {
      sessions,
      compteurs: {
        conforme: compter(CategorieAuditBpf.Conforme),
        alerte: compter(CategorieAuditBpf.Alerte),
        bloquante: compter(CategorieAuditBpf.Bloquante),
        exclue: compter(CategorieAuditBpf.Exclue),
      },
    };
  });
  /** Gestionnaires présents dans l'audit (triés par nom), "Sans gestionnaire" en dernier. */
  public gestionnairesDisponibles = computed<GestionnaireAudit[]>(() => {
    const parId = new Map<number, GestionnaireAudit>();
    for (const session of this.auditGlobal()?.sessions ?? []) {
      const id = session.idGestionnaireAdministratif ?? SANS_GESTIONNAIRE;
      const libelle = id === SANS_GESTIONNAIRE ? 'Sans gestionnaire' : session.libelleGestionnaireAdministratif || `Gestionnaire n°${id}`;
      const gestionnaire = parId.get(id) ?? { id, libelle, nombreSessions: 0 };
      gestionnaire.nombreSessions++;
      parId.set(id, gestionnaire);
    }
    return Array.from(parId.values()).sort((a, b) =>
      a.id === SANS_GESTIONNAIRE ? 1 : b.id === SANS_GESTIONNAIRE ? -1 : a.libelle.localeCompare(b.libelle, 'fr'),
    );
  });
  /** Déplie la liste complète des pastilles de motifs au-delà des 6 premières ("+N autres"). */
  public motifsDeplies = signal(false);
  /** "Voir plus" : même principe que l'ancienne pagination (10 de plus à chaque clic), mais cumulatif plutôt que remplacé page par page. */
  public nombreResultatsAffiches = signal(this.TAILLE_PAGE_AUDIT);
  public pastilleCopieVisible = signal(false);
  /** Passe à true juste après l'arrivée d'un nouveau résumé, pour déclencher l'animation d'entrée de la synthèse visuelle (le rendu initial doit d'abord se peindre à false, sinon la transition CSS ne joue pas). */
  public syntheseAnimee = signal(false);
  /** Progression (0 à 1, avec easing) du compteur des chiffres de la synthèse visuelle : multiplier une valeur cible par cette progression donne sa valeur affichée à l'instant courant. */
  public progressionSynthese = signal(0);
  /**
   * Part des sessions « Conforme » parmi les sessions évaluées (Conforme + Alerte + Bloquante).
   * Les sessions « Exclue » sont hors périmètre BPF par nature (brouillon, sans date...) et ne
   * comptent pas dans le calcul, pour ne pas pénaliser le score à cause de sessions qui ne
   * devraient de toute façon pas être déclarées. Seuils de couleur (BON/MOYEN) calibrés
   * arbitrairement faute de référence métier — à ajuster si besoin.
   */
  private readonly SEUIL_FIABILITE_BON = 80;
  private readonly SEUIL_FIABILITE_MOYEN = 50;
  public pourcentageFiabilite = computed<number | null>(() => {
    const audit = this.auditAffiche();
    if (!audit) {
      return null;
    }
    const { conforme, alerte, bloquante } = audit.compteurs;
    const total = conforme + alerte + bloquante;
    return total > 0 ? Math.round((conforme / total) * 100) : null;
  });
  /** Organisme de formation de l'entité sélectionnée (cadre A) ; null si aucun organisme n'est paramétré pour cette entité. */
  public organisme = signal<OrganismeFormationModel | null>(null);
  public etatOrganisme = signal<EtatOrganisme>('chargement');
  /** Libellés des entités sélectionnées dont l'organisme diffère de celui affiché au cadre A. */
  public entitesOrganismeDifferent = signal<string[]>([]);
  /** Le lien vers le paramétrage de l'organisme n'est proposé qu'aux utilisateurs ayant le droit d'y accéder. */
  public peutParametrerOrganisme = signal(false);
  public lignesIdentification = computed<LigneIdentification[]>(() => {
    const organisme = this.organisme();
    return [
      { libelle: 'Numéro de déclaration', valeur: this.texteOuNull(organisme?.numeroActivite) },
      { libelle: 'Forme juridique', valeur: null, indisponible: true },
      { libelle: 'Numéro de SIRET', valeur: this.texteOuNull(organisme?.siret) },
      { libelle: 'Code NAF', valeur: null, indisponible: true },
      { libelle: 'Nom et prénom ou dénomination (sigle)', valeur: this.texteOuNull(organisme?.nom) },
      { libelle: 'Adresse', valeur: this.formaterAdresse(organisme) },
      { libelle: 'Acceptez-vous que cette adresse soit rendue publique', valeur: null, indisponible: true },
      { libelle: 'Tél.', valeur: this.texteOuNull(organisme?.telephone) },
      { libelle: 'Email de contact', valeur: this.texteOuNull(organisme?.mail) },
    ];
  });
  public organismeIncomplet = computed(() => this.lignesIdentification().some((ligne) => !ligne.valeur && !ligne.indisponible));
  /** Synthèse pédagogique (cadres E, F et G) : recalculée seulement quand le résumé change, pas à chaque image de l'animation. */
  public synthesePedagogique = computed(() => {
    const r = this.resume();
    if (!r) {
      return null;
    }
    // Rangée 1 : chiffres clés (E, F, G) ; rangée 2 : répartitions (F-1, F-3, F-4).
    return [
      this.panneauFormateurs(r.blocFormateurs),
      this.panneauStagiaires(r),
      this.panneauFormationsConfiees(r),
      this.panneauRepartition('F - 1', 'Type de stagiaires', r.blocF1),
      this.panneauRepartition('F - 3', 'Objectif général des prestations', this.lignesSyntheseF3(r.blocF3)),
      this.panneauRepartition('F - 4', 'Spécialités de formation', r.blocF4),
    ];
  });
  private cartesRepliees = new Set<string>();
  private sessionsDepliees = new Set<number>();
  private pastilleCopieTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private syntheseAnimationTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private compteurSyntheseIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly DUREE_COMPTEUR_SYNTHESE_MS = 1000;
  private readonly PAS_COMPTEUR_SYNTHESE_MS = 16;
  /** Index des onglets Financier (1) et Pédagogique (2) dans ejs-tab. */
  private readonly ONGLETS_GRAPHIQUES_ANIMES = [1, 2];

  private destroyRef = inject(DestroyRef);

  constructor(
    private entitesService: EntitesService,
    private bpfResumeService: BpfResumeService,
    private bpfAuditService: BpfAuditService,
    private navigationService: NavigationService,
    private breadcrumbsService: BreadcrumbsService,
    private bpfEcranStateService: BpfEcranStateService,
    private notificationToastService: NotificationToastService,
    private organismeFormationService: OrganismeFormationService,
    private utilisateurCourantService: UtilisateurCourantService,
    private router: Router,
    public modalService: ModalService
  ) {
    this.destroyRef.onDestroy(() => {
      this.observateurCartes?.disconnect();
      if (this.pastilleCopieTimeoutId) {
        clearTimeout(this.pastilleCopieTimeoutId);
      }
      if (this.syntheseAnimationTimeoutId) {
        clearTimeout(this.syntheseAnimationTimeoutId);
      }
      if (this.compteurSyntheseIntervalId) {
        clearInterval(this.compteurSyntheseIntervalId);
      }
    });
  }

  ngOnInit(): void {
    const etatRestaure = this.bpfEcranStateService.aUnEtatSauvegarde();

    if (etatRestaure) {
      this.anneeSelectionnee = this.bpfEcranStateService.anneeSelectionnee;
      this.dateDebut = this.bpfEcranStateService.dateDebut;
      this.dateFin = this.bpfEcranStateService.dateFin;
      this.categoriesSelectionnees.set(new Set(this.bpfEcranStateService.categoriesSelectionnees ?? this.CATEGORIES_PAR_DEFAUT));
      this.motifSelectionne.set(this.bpfEcranStateService.motifSelectionne);
      this.gestionnaireSelectionne.set(this.bpfEcranStateService.gestionnaireSelectionne);
      this.motifsDeplies.set(this.bpfEcranStateService.motifsDeplies);
      this.nombreResultatsAffiches.set(this.bpfEcranStateService.nombreResultatsAffiches ?? this.TAILLE_PAGE_AUDIT);
    }

    this.utilisateurCourantService
      .getDroitsUtilisateur()
      .subscribe((droits) =>
        this.peutParametrerOrganisme.set(droits.some((droit) => droit.codeFonctionLogicielle === FonctionLogicielle.ParametresEntite))
      );

    // Sélection par défaut : l'état restauré prime, sinon l'entité de travail globale actuelle
    // (bandeau du haut, lue de façon synchrone sur le BehaviorSubject).
    const idEntiteGlobale = this.entitesService.idEntiteSelectionneeEvent.value;
    const idsEntiteParDefaut = etatRestaure
      ? this.bpfEcranStateService.idsEntiteSelectionnees
      : idEntiteGlobale != null ? [idEntiteGlobale] : [];

    // Suit l'entité de travail globale en continu (même pattern que liste-formations,
    // liste-personnes, etc.), en lecture seule — ne modifie jamais cette entité globale depuis
    // le BPF. On ignore la valeur déjà retenue comme défaut ci-dessus (pour ne pas écraser un
    // état restauré avec la ré-émission immédiate du BehaviorSubject au subscribe) ainsi que
    // tout changement reçu tant que `entites` (dataSource du sélecteur) n'est pas encore
    // chargé — le widget Syncfusion ne peut pas résoudre le texte à afficher pour une valeur
    // fixée avant que ses données soient disponibles, et ne se corrige pas tout seul après coup.
    this.entitesService.idEntiteSelectionneeEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((idEntite) => {
        if (idEntite != null && !idsEntiteParDefaut.includes(idEntite) && this.entites.length > 0) {
          this.idsEntiteSelectionnees = [idEntite];
        }
      });

    this.entitesService.getUtilisateursEntitesGerables().subscribe((entites) => {
      this.entites = entites.filter((entite) => entite.isEntiteTravail && entite.active);
      this.idsEntiteSelectionnees = idsEntiteParDefaut;
      // Rechargé en direct plutôt que restauré depuis un instantané : au retour d'une navigation
      // (paramétrage de l'organisme, correction d'une erreur d'audit...), l'écran doit refléter
      // l'état réel, pas une photo d'avant ce changement.
      if (etatRestaure) {
        this.chargerResumeEtAudit();
      }
    });
  }

  /**
   * ejs-multiselect émet aussi ngModelChange quand sa valeur est fixée par le code (restauration au
   * retour d'une fiche) : sans ce garde, les filtres d'audit restaurés (catégories, motif,
   * gestionnaire) étaient aussitôt remis à zéro par viderResume().
   */
  public onEntitesChange(idsEntite: number[] | null): void {
    const nouvelles = idsEntite ?? [];
    if (this.memesEntites(nouvelles, this.idsEntiteSelectionnees)) {
      return;
    }
    this.idsEntiteSelectionnees = nouvelles;
    this.viderResume();
  }

  private memesEntites(a: number[], b: number[]): boolean {
    return a.length === b.length && a.every((id) => b.includes(id));
  }

  public onAnneeChange(): void {
    this.dateDebut = this.calculerDateDebutParDefaut(this.anneeSelectionnee);
    this.dateFin = this.calculerDateFinParDefaut(this.anneeSelectionnee);
    this.viderResume();
  }

  /** Même garde que onEntitesChange : la période restaurée ne doit pas effacer les filtres d'audit. */
  public onPeriodeChange(event: RangeEventArgs): void {
    const dateDebut = (event.startDate as Date) ?? null;
    const dateFin = (event.endDate as Date) ?? null;
    if (dateDebut?.getTime() === this.dateDebut?.getTime() && dateFin?.getTime() === this.dateFin?.getTime()) {
      return;
    }
    this.dateDebut = dateDebut;
    this.dateFin = dateFin;
    this.viderResume();
  }

  /** Le bouton "Générer le résumé" reste désactivé tant qu'au moins une entité, une année et une période ne sont pas sélectionnées. */
  public peutGenererResume(): boolean {
    return this.idsEntiteSelectionnees.length > 0 && this.anneeSelectionnee != null && this.dateDebut != null && this.dateFin != null;
  }

  public genererResume(): void {
    this.categoriesSelectionnees.set(new Set(this.CATEGORIES_PAR_DEFAUT));
    this.motifSelectionne.set(null);
    this.gestionnaireSelectionne.set(null);
    this.motifsDeplies.set(false);
    this.nombreResultatsAffiches.set(this.TAILLE_PAGE_AUDIT);
    this.chargerResumeEtAudit();
  }

  /**
   * Récupère le résumé + l'audit global pour les filtres entité/période actuels, sans toucher aux
   * catégories/motif cochés — contrairement à genererResume(), qui les réinitialise. Utilisé pour
   * le "Générer le résumé" explicite ET pour le retour d'une navigation (correction d'une erreur
   * d'audit, paramétrage de l'organisme...), où l'écran doit refléter l'état réel plutôt qu'un
   * instantané d'avant le changement.
   */
  private chargerResumeEtAudit(): void {
    this.syntheseAnimee.set(false);
    this.bpfResumeService
      .getResume(this.idsEntiteSelectionnees, this.dateDebut, this.dateFin)
      .subscribe((resume) => {
        this.resume.set(resume);
        this.declencherAnimationSynthese();
      });
    this.chargerOrganisme();
    this.bpfAuditService
      .getAuditGlobal(this.idsEntiteSelectionnees, this.dateDebut, this.dateFin)
      .subscribe((auditGlobal) => this.auditGlobal.set(auditGlobal));
  }

  /** Le résumé affiché ne doit jamais correspondre à des filtres différents de ceux sélectionnés : dès qu'un filtre change, on l'efface jusqu'au prochain "Générer le résumé". */
  private viderResume(): void {
    this.resume.set(null);
    this.auditGlobal.set(null);
    this.organisme.set(null);
    this.entitesOrganismeDifferent.set([]);
    this.etatOrganisme.set('chargement');
    this.categoriesSelectionnees.set(new Set(this.CATEGORIES_PAR_DEFAUT));
    this.motifSelectionne.set(null);
    this.gestionnaireSelectionne.set(null);
    this.motifsDeplies.set(false);
    this.nombreResultatsAffiches.set(this.TAILLE_PAGE_AUDIT);
    this.syntheseAnimee.set(false);
    this.progressionSynthese.set(0);
    if (this.syntheseAnimationTimeoutId) {
      clearTimeout(this.syntheseAnimationTimeoutId);
      this.syntheseAnimationTimeoutId = null;
    }
    if (this.compteurSyntheseIntervalId) {
      clearInterval(this.compteurSyntheseIntervalId);
      this.compteurSyntheseIntervalId = null;
    }
  }

  /** Onglets Financier et Pédagogique : leurs graphiques sont masqués à la génération du résumé, l'animation se rejoue donc à leur ouverture. */
  public onOngletSelectionne(event: SelectEventArgs): void {
    if (this.ONGLETS_GRAPHIQUES_ANIMES.includes(event.selectedIndex) && this.resume()) {
      this.syntheseAnimee.set(false);
      this.progressionSynthese.set(0);
      this.declencherAnimationSynthese();
    }
  }

  /** Laisse le premier rendu se peindre à l'état initial (non animé) avant de basculer la classe qui déclenche les transitions CSS d'entrée de la synthèse visuelle, puis démarre le compteur des chiffres. */
  private declencherAnimationSynthese(): void {
    if (this.syntheseAnimationTimeoutId) {
      clearTimeout(this.syntheseAnimationTimeoutId);
    }
    this.syntheseAnimationTimeoutId = setTimeout(() => {
      this.syntheseAnimee.set(true);
      this.lancerCompteurSynthese();
    }, 30);
  }

  /** Anime la progression (0 à 1, easing "ease-out cubic") utilisée pour faire compter les chiffres de la synthèse visuelle jusqu'à leur valeur finale. */
  private lancerCompteurSynthese(): void {
    if (this.compteurSyntheseIntervalId) {
      clearInterval(this.compteurSyntheseIntervalId);
    }
    const nombreDePas = Math.round(this.DUREE_COMPTEUR_SYNTHESE_MS / this.PAS_COMPTEUR_SYNTHESE_MS);
    let pasEcoules = 0;
    this.compteurSyntheseIntervalId = setInterval(() => {
      pasEcoules++;
      const t = Math.min(1, pasEcoules / nombreDePas);
      this.progressionSynthese.set(1 - Math.pow(1 - t, 3));
      if (t >= 1) {
        clearInterval(this.compteurSyntheseIntervalId);
        this.compteurSyntheseIntervalId = null;
      }
    }, this.PAS_COMPTEUR_SYNTHESE_MS);
  }

  /** Valeur affichée à l'instant courant de l'animation de comptage (arrondie), pour une valeur cible donnée. */
  public valeurAnimee(valeurCible: number): number {
    return Math.round(valeurCible * this.progressionSynthese());
  }

  /** Coche/décoche une catégorie (multi-sélection) parmi celles affichées dans la liste ci-dessous. */
  public filtrerParCategorie(categorie: CategorieAuditBpf): void {
    this.categoriesSelectionnees.update((categories) => {
      // "Conforme" ouvre une vue dédiée (cartes des sessions conformes) exclusive des catégories à
      // corriger ; un second clic revient aux catégories par défaut. Le filtre gestionnaire est conservé.
      if (categorie === CategorieAuditBpf.Conforme) {
        return categories.has(categorie) ? new Set(this.CATEGORIES_PAR_DEFAUT) : new Set([categorie]);
      }
      const nouvelles = new Set(categories);
      nouvelles.delete(CategorieAuditBpf.Conforme);
      if (nouvelles.has(categorie)) {
        nouvelles.delete(categorie);
      } else {
        nouvelles.add(categorie);
      }
      return nouvelles;
    });
    this.motifSelectionne.set(null);
    this.motifsDeplies.set(false);
    this.nombreResultatsAffiches.set(this.TAILLE_PAGE_AUDIT);
  }

  /** Le motif choisi peut ne plus exister chez le nouveau gestionnaire : on repart de "Tous les motifs". */
  public filtrerParGestionnaire(valeur: string): void {
    this.gestionnaireSelectionne.set(valeur === '' ? null : Number(valeur));
    this.motifSelectionne.set(null);
    this.motifsDeplies.set(false);
    this.nombreResultatsAffiches.set(this.TAILLE_PAGE_AUDIT);
  }

  /** Sélection unique : cliquer la pastille déjà active revient à "Tous les motifs" (motif null). */
  public filtrerParMotif(motif: string | null): void {
    this.motifSelectionne.set(motif !== null && this.motifSelectionne() === motif ? null : motif);
    this.nombreResultatsAffiches.set(this.TAILLE_PAGE_AUDIT);
  }

  public deplierMotifs(): void {
    this.motifsDeplies.set(true);
  }

  /**
   * Sessions des catégories cochées, avant filtre par motif — sert aussi de base à
   * motifsDisponibles. Conforme reste exclue même si cochée : ces sessions n'ont structurellement
   * rien à corriger et ne sont jamais listées (seul leur compteur est affiché).
   */
  private sessionsParCategorie(auditGlobal: BpfAuditGlobal): BpfAuditSession[] {
    const categories = this.categoriesSelectionnees();
    return auditGlobal.sessions.filter(
      (session) => session.categorie !== CategorieAuditBpf.Conforme && categories.has(session.categorie),
    );
  }

  /** Les sessions Conformes ne nécessitent aucune correction : elles ne sont jamais listées, seul leur compteur est affiché. */
  public sessionsFiltrees(auditGlobal: BpfAuditGlobal): BpfAuditSession[] {
    const sessions = this.sessionsParCategorie(auditGlobal);
    const motif = this.motifSelectionne();
    return motif ? sessions.filter((session) => session.erreurs.some((erreur) => erreur.message === motif)) : sessions;
  }

  /** Nombre de sessions des catégories cochées, avant filtre par motif — compteur de la pastille "Tous les motifs". */
  public totalSessionsCategorie(auditGlobal: BpfAuditGlobal): number {
    return this.sessionsParCategorie(auditGlobal).length;
  }

  /**
   * Motifs d'anomalie présents dans la catégorie actuellement affichée, avec le nombre de
   * sessions distinctes concernées par chacun (pas le nombre brut d'erreurs — une session peut
   * cumuler plusieurs erreurs au même message). Triés par nombre de sessions décroissant.
   * Une même session peut apparaître dans plusieurs motifs à la fois (erreurs multiples) : la
   * somme des compteurs par motif peut donc dépasser le total de la catégorie, c'est attendu.
   */
  public motifsDisponibles(auditGlobal: BpfAuditGlobal): { message: string; nombreSessions: number }[] {
    const sessionsParMessage = new Map<string, Set<number>>();
    for (const session of this.sessionsParCategorie(auditGlobal)) {
      for (const erreur of session.erreurs) {
        const sessions = sessionsParMessage.get(erreur.message) ?? new Set<number>();
        sessions.add(session.idSession);
        sessionsParMessage.set(erreur.message, sessions);
      }
    }
    return Array.from(sessionsParMessage.entries())
      .map(([message, sessions]) => ({ message, nombreSessions: sessions.size }))
      .sort((a, b) => b.nombreSessions - a.nombreSessions);
  }

  private readonly NOMBRE_MOTIFS_VISIBLES = 6;

  /** Les 6 premiers motifs si repliés et qu'il y en a plus de 7, sinon tous. */
  public motifsAffiches(auditGlobal: BpfAuditGlobal): { message: string; nombreSessions: number }[] {
    const motifs = this.motifsDisponibles(auditGlobal);
    if (motifs.length <= this.NOMBRE_MOTIFS_VISIBLES + 1 || this.motifsDeplies()) {
      return motifs;
    }
    return motifs.slice(0, this.NOMBRE_MOTIFS_VISIBLES);
  }

  /** Nombre de motifs masqués par la pastille "+N autres" (0 si rien à masquer ou déjà déplié). */
  public nombreMotifsMasques(auditGlobal: BpfAuditGlobal): number {
    const motifs = this.motifsDisponibles(auditGlobal);
    return motifs.length > this.NOMBRE_MOTIFS_VISIBLES + 1 && !this.motifsDeplies() ? motifs.length - this.NOMBRE_MOTIFS_VISIBLES : 0;
  }

  /** Les N premiers résultats de la liste filtrée, N grandissant à chaque "Voir plus". */
  public resultatsAffiches(auditGlobal: BpfAuditGlobal): BpfAuditSession[] {
    return this.sessionsFiltrees(auditGlobal).slice(0, this.nombreResultatsAffiches());
  }

  /** Vue dédiée aux sessions conformes, affichée quand le filtre "Conforme" est choisi. */
  public modeConforme = computed(() => this.categoriesSelectionnees().has(CategorieAuditBpf.Conforme));

  public sessionsConformes(auditGlobal: BpfAuditGlobal): BpfAuditSession[] {
    return auditGlobal.sessions.filter((session) => session.categorie === CategorieAuditBpf.Conforme);
  }

  public conformesAffichees(auditGlobal: BpfAuditGlobal): BpfAuditSession[] {
    return this.sessionsConformes(auditGlobal).slice(0, this.nombreConformesAffichees());
  }

  /** Nombre de colonnes réellement affichées par la grille des cartes (auto-fill CSS, dépend de la largeur d'écran). */
  public colonnesCartes = signal(1);
  private observateurCartes?: ResizeObserver;
  private readonly zone = inject(NgZone);

  @ViewChild('grilleCartes') private set grilleCartes(ref: ElementRef<HTMLElement> | undefined) {
    this.observateurCartes?.disconnect();
    this.observateurCartes = undefined;
    if (!ref || typeof ResizeObserver === 'undefined') {
      return;
    }
    const grille = ref.nativeElement;
    // Mesure dans le callback (asynchrone) plutôt que pendant la détection de changements.
    this.observateurCartes = new ResizeObserver(() =>
      this.zone.run(() =>
        this.colonnesCartes.set(Math.max(1, getComputedStyle(grille).gridTemplateColumns.split(' ').filter(Boolean).length))
      )
    );
    this.observateurCartes.observe(grille);
  }

  /** Arrondi au multiple supérieur du nombre de colonnes : la dernière ligne de cartes affichées est toujours complète. */
  public nombreConformesAffichees(): number {
    const colonnes = this.colonnesCartes();
    return Math.ceil(this.nombreResultatsAffiches() / colonnes) * colonnes;
  }

  @ViewChild('tooltipCartes') private tooltipCartes?: TooltipComponent;

  /** N'affiche la tooltip d'une carte conforme que si son libellé est tronqué par l'ellipse, avec le libellé complet. */
  public preparerTooltipCarte(args: TooltipEventArgs): void {
    const titre = args.target?.querySelector<HTMLElement>('.bpf-audit__carte-titre');
    if (!titre || titre.scrollWidth <= titre.clientWidth) {
      args.cancel = true;
      return;
    }
    if (this.tooltipCartes) {
      this.tooltipCartes.content = titre.textContent.trim();
    }
  }

  public voirSession(idSession: number): void {
    this.allerVersAudit({ type: 'session-general', idSession });
  }

  public voirPlus(): void {
    this.nombreResultatsAffiches.update((nombre) => nombre + this.TAILLE_PAGE_AUDIT);
  }

  public toggleErreurs(idSession: number): void {
    if (this.sessionsDepliees.has(idSession)) {
      this.sessionsDepliees.delete(idSession);
    } else {
      this.sessionsDepliees.add(idSession);
    }
  }

  public erreursDepliees(idSession: number): boolean {
    return this.sessionsDepliees.has(idSession);
  }

  /** Ferme la popup de détail (si ouverte) et redirige directement vers l'entité à corriger (contrat, formateur, inscription, séance) quand elle est identifiable, sinon vers l'onglet de la session concernée. */
  public allerVersAudit(navigation: BpfAuditNavigation): void {
    this.modalService.closeModal(this.modalType);
    this.memoriserEcranAvantNavigation();

    switch (navigation.type) {
      case 'session-general':
        this.navigationService.sessions.general.navigate(navigation.idSession);
        break;
      case 'session-inscriptions':
        this.navigationService.sessions.inscriptionsByIdSession.navigate(navigation.idSession);
        break;
      case 'session-seances':
        this.navigationService.sessions.seancesByIdSession.navigate(navigation.idSession);
        break;
      case 'session-tarifs':
        this.navigationService.sessions.tarifsByIdSession.navigate(navigation.idSession);
        break;
      case 'contrat-session':
        this.navigationService.dossiersFinancement.edition_contrat_session.navigate(navigation.idDossierFinancement, navigation.idSession);
        break;
      case 'inscription':
        this.navigationService.inscriptions.edition_general.navigate(navigation.idInscription);
        break;
      case 'formateur':
        this.navigationService.formateurs.editionGeneral.navigate(navigation.idFormateur);
        break;
      case 'seance':
        // seances.edition (fiche séance autonome) n'a pas de route enregistrée côté front (404
        // garanti) : la saisie des présences est toujours imbriquée sous sa session.
        this.navigationService.seances.saisieAbsencesSessions.navigate(navigation.idSession, navigation.idSeance);
        break;
      case 'formation':
        // Hypothèse : formation "simple" (fiche générale). Les formations composées ne sont pas
        // distinguées ici faute d'info dans le résumé/audit BPF — à corriger si besoin une fois
        // le vrai champ de type de formation disponible côté back.
        this.navigationService.formations.edition_simple.navigate(navigation.idFormation);
        break;
    }
  }

  /** Le financeur s'affiche en puce non cliquable (pas de fiche financement dédiée) : la ligne principale mène au contrat de la session, l'idInscription renvoyé par le back est quasi toujours nul sur le périmètre réel BPF. */
  public voirDetailLigneFinanciere(ligne: BpfLigneFinanciere): void {
    const detail: BpfDetail = {
      titre: `Détail: ${ligne.libelle}`,
      lignes: ligne.financements.map((financement) => ({
        principal: this.valeurContratSession(financement),
        secondaires: [{ texte: financement.libelleFinanceur }],
        valeur: this.formatMontant(financement.montantHT),
      })),
    };

    this.modalService.openModal(this.modalType, detail);
  }

  private valeurContratSession(financement: BpfFinancementDetail): BpfDetailValeur {
    return {
      texte: financement.libelleSession,
      navigation: { type: 'contrat-session', idDossierFinancement: financement.idDossierFinancement, idSession: financement.idSession },
    };
  }

  public voirDetailFormateurs(titre: string, effectif: BpfEffectifFormateurs): void {
    const detail: BpfDetail = {
      titre: `Détail: ${titre}`,
      lignes: effectif.formateurs.map((formateur) => ({
        principal: this.valeurFormateur(formateur),
        valeur: `${formateur.heuresDispensees} h`,
      })),
    };

    this.modalService.openModal(this.modalType, detail);
  }

  /** Lignes du CERFA, sans la ligne "Non renseigné" (hors CERFA et hors total, affichée sous le TOTAL). */
  public lignesCerfa(lignes: BpfRepartition[]): BpfRepartition[] {
    return lignes.filter((ligne) => ligne.cle !== CLE_NON_RENSEIGNE);
  }

  public lignesNonRenseignees(lignes: BpfRepartition[]): BpfRepartition[] {
    return lignes.filter((ligne) => ligne.cle === CLE_NON_RENSEIGNE);
  }

  /** Pourcentage arrondi à l'unité ; 0 si le total est nul. */
  public pourcentage(valeur: number, total: number): number {
    return total ? Math.round((valeur / total) * 100) : 0;
  }

  /** Cadre E : formateurs internes / sous-traitants, part en heures dispensées. */
  public panneauFormateurs(bloc: BpfBlocFormateurs): PanneauSynthesePedagogique {
    const heures = bloc.interne.heuresDispensees + bloc.sousTraitant.heuresDispensees;
    const pctExterne = this.pourcentage(bloc.sousTraitant.heuresDispensees, heures);
    return {
      cadre: 'Cadre E',
      titre: 'Heures dispensées par les formateurs',
      chiffres: [
        { valeur: bloc.interne.nombreDistinct + bloc.sousTraitant.nombreDistinct, suffixe: '', libelle: 'formateurs' },
        { valeur: heures, suffixe: ' h', libelle: 'dispensées' },
        { valeur: pctExterne, suffixe: ' %', libelle: 'sous-traitées' },
      ],
      baseDuPourcentage: 'des heures dispensées',
      parts: [
        {
          libelle: 'Internes', nombre: bloc.interne.nombreDistinct, unite: 'form.', heures: bloc.interne.heuresDispensees,
          pct: this.pourcentage(bloc.interne.heuresDispensees, heures), couleur: COULEUR_INTERNE,
          detail: () => this.voirDetailFormateurs('Personnes de votre organisme dispensant des heures de formation', bloc.interne),
        },
        {
          libelle: 'Externes', nombre: bloc.sousTraitant.nombreDistinct, unite: 'form.', heures: bloc.sousTraitant.heuresDispensees,
          pct: pctExterne, couleur: COULEUR_EXTERNE,
          detail: () => this.voirDetailFormateurs(
            'Personnes extérieures à votre organisme dispensant des heures de formation dans le cadre de contrats de sous-traitance',
            bloc.sousTraitant,
          ),
        },
      ],
    };
  }

  /** Cadre F : total F-1, dont F-2 (activité confiée à un autre organisme, sous-ensemble de F-1), part en heures. */
  public panneauStagiaires(r: BpfResume): PanneauSynthesePedagogique {
    const heuresInterne = r.totalF1.heuresFormation - r.blocF2.heuresFormation;
    const pctConfie = this.pourcentage(r.blocF2.heuresFormation, r.totalF1.heuresFormation);
    return {
      cadre: 'Cadre F',
      titre: 'Stagiaires de l’organisme',
      chiffres: [
        { valeur: r.totalF1.nombreStagiaires, suffixe: '', libelle: 'stagiaires' },
        { valeur: r.totalF1.heuresFormation, suffixe: ' h', libelle: 'de formation' },
        { valeur: pctConfie, suffixe: ' %', libelle: 'confiées à un autre OF (F - 2)' },
      ],
      baseDuPourcentage: 'des heures de formation',
      parts: [
        {
          libelle: 'Réalisé par l’organisme', nombre: r.totalF1.nombreStagiaires - r.blocF2.nombreStagiaires, unite: 'stag.',
          heures: heuresInterne, pct: this.pourcentage(heuresInterne, r.totalF1.heuresFormation), couleur: COULEUR_INTERNE,
        },
        {
          libelle: 'Confié à un autre OF (F - 2)', nombre: r.blocF2.nombreStagiaires, unite: 'stag.', heures: r.blocF2.heuresFormation,
          pct: pctConfie, couleur: COULEUR_EXTERNE, detail: () => this.voirDetailTotal('F - 2', r.blocF2),
        },
      ],
    };
  }

  /** Cadre G : stagiaires confiés à l'organisme par un autre OF. Exclus de F-1, donc rapportés à F-1 + G (activité totale), en heures. */
  public panneauFormationsConfiees(r: BpfResume): PanneauSynthesePedagogique {
    const heures = r.totalF1.heuresFormation + r.blocG.heuresFormation;
    const pctG = this.pourcentage(r.blocG.heuresFormation, heures);
    return {
      cadre: 'Cadre G',
      titre: 'Formations confiées par un autre organisme',
      chiffres: [
        { valeur: r.blocG.nombreStagiaires, suffixe: '', libelle: 'stagiaires' },
        { valeur: r.blocG.heuresFormation, suffixe: ' h', libelle: 'de formation' },
        { valeur: pctG, suffixe: ' %', libelle: 'de l’activité totale' },
      ],
      baseDuPourcentage: 'des heures (F - 1 + G)',
      parts: [
        {
          libelle: 'Activité propre (F - 1)', nombre: r.totalF1.nombreStagiaires, unite: 'stag.', heures: r.totalF1.heuresFormation,
          pct: this.pourcentage(r.totalF1.heuresFormation, heures), couleur: COULEUR_INTERNE,
        },
        {
          libelle: 'Pour un autre OF (G)', nombre: r.blocG.nombreStagiaires, unite: 'stag.', heures: r.blocG.heuresFormation,
          pct: pctG, couleur: COULEUR_EXTERNE, detail: () => this.voirDetailTotal('G', r.blocG),
        },
      ],
    };
  }

  /** Panneau F-1 / F-3 / F-4 : pas de chiffres clés (le total est celui du cadre F), seulement la répartition. */
  public panneauRepartition(code: string, titre: string, lignes: BpfRepartition[]): PanneauSynthesePedagogique {
    return {
      cadre: `Cadre ${code}`,
      titre,
      chiffres: [],
      baseDuPourcentage: 'des stagiaires',
      parts: this.partsRepartition(code, lignes),
    };
  }

  /**
   * Barre F-1 / F-3 / F-4 : une part par ligne, en part des stagiaires. Couleur fixée par la position de la ligne
   * (stable même si une ligne vide est masquée), gris pour "Non renseigné". Les lignes sans stagiaire sont masquées.
   */
  public partsRepartition(titre: string, lignes: BpfRepartition[]): PartSynthesePedagogique[] {
    const total = lignes.reduce((somme, ligne) => somme + ligne.nombreStagiaires, 0);
    return lignes
      .map((ligne, index) => ({
        libelle: ligne.libelle,
        nombre: ligne.nombreStagiaires,
        unite: 'stag.',
        heures: ligne.heuresFormation,
        pct: this.pourcentage(ligne.nombreStagiaires, total),
        couleur: ligne.cle === CLE_NON_RENSEIGNE ? COULEUR_NON_RENSEIGNE : COULEURS_REPARTITION[index % COULEURS_REPARTITION.length],
        detail: () => this.voirDetailRepartition(titre, ligne),
      }))
      .filter((part) => part.nombre > 0);
  }

  /**
   * Barres F-3 de la synthèse : ligne a agrégée (sans ses sous-niveaux RNCP), lignes b à f,
   * puis "Non renseigné" en dernier, comme F-4.
   */
  public lignesSyntheseF3(blocF3: BpfBlocF3): BpfRepartition[] {
    return [blocF3.ligneA, ...this.lignesCerfa(blocF3.autresLignes), ...this.lignesNonRenseignees(blocF3.autresLignes)];
  }

  /** Notice CERFA : les totaux F-3 et F-4 doivent être égaux au total F-1, en stagiaires comme en heures. */
  public ecartAvecTotalF1(resume: BpfResume, total: BpfTotalCadre): boolean {
    return total.nombreStagiaires !== resume.totalF1.nombreStagiaires || total.heuresFormation !== resume.totalF1.heuresFormation;
  }

  public voirDetailRepartition(titre: string, ligne: BpfRepartition): void {
    this.voirDetailInscriptions(`${titre} — ${ligne.libelle}`, ligne.inscriptions);
  }

  public voirDetailTotal(titre: string, total: BpfTotalStagiaires): void {
    this.voirDetailInscriptions(titre, total.inscriptions);
  }

  /** Ferme la popup de détail et redirige vers la fiche réelle (session, participant ou formateur), avec un retour possible vers l'écran BPF qui conserve filtres et résumé déjà généré. */
  public allerVers(navigation: BpfDetailNavigation): void {
    this.modalService.closeModal(this.modalType);
    this.memoriserEcranAvantNavigation();

    switch (navigation.type) {
      case 'session':
        this.navigationService.sessions.edition_root.navigate(navigation.id);
        break;
      case 'inscription':
        this.navigationService.inscriptions.edition_general.navigate(navigation.id);
        break;
      case 'formateur':
        this.navigationService.formateurs.editionGeneral.navigate(navigation.id);
        break;
      case 'contrat-session':
        this.navigationService.dossiersFinancement.edition_contrat_session.navigate(navigation.idDossierFinancement, navigation.idSession);
        break;
    }
  }

  /** Mène au paramétrage de l'organisme (qui s'ouvre sur l'entité de travail globale du bandeau), avec un retour possible vers l'écran BPF qui recharge alors le cadre A. */
  public allerVersParametrageOrganisme(): void {
    this.memoriserEcranAvantNavigation();
    this.router.navigate(['/', AppPath.PARAMETRES, 'organisme-formation']);
  }

  /** Conserve filtres (dont catégories/motif d'audit cochés) et fait pointer le fil d'Ariane de la page cible vers l'écran BPF. */
  private memoriserEcranAvantNavigation(): void {
    this.breadcrumbsService.setReturnUrlOverride(this.buildReturnUrl());
    this.bpfEcranStateService.sauvegarder({
      idsEntiteSelectionnees: this.idsEntiteSelectionnees,
      anneeSelectionnee: this.anneeSelectionnee,
      dateDebut: this.dateDebut,
      dateFin: this.dateFin,
      categoriesSelectionnees: this.categoriesSelectionnees(),
      motifSelectionne: this.motifSelectionne(),
      gestionnaireSelectionne: this.gestionnaireSelectionne(),
      motifsDeplies: this.motifsDeplies(),
      nombreResultatsAffiches: this.nombreResultatsAffiches(),
    });
  }

  /** L'erreur HTTP est déjà affichée par la chaîne d'intercepteurs : on se contente de signaler l'échec dans le cadre A, sans toucher aux autres cadres. */
  /**
   * Cadre A : organisme de la première entité sélectionnée (ordre de la liste des entités). Avec
   * plusieurs entités, les organismes des autres sont chargés aussi, pour signaler celles qui n'ont pas
   * le même n° de déclaration ou SIRET (un BPF se dépose par n° de déclaration d'activité).
   */
  private chargerOrganisme(): void {
    this.organisme.set(null);
    this.entitesOrganismeDifferent.set([]);
    this.etatOrganisme.set('chargement');
    const idsOrdonnes = this.idsEntiteOrdonnes();
    forkJoin(idsOrdonnes.map((idEntite) => this.organismeFormationService.getOrganismeFormation(idEntite)))
      .pipe(
        catchError(() => {
          this.etatOrganisme.set('erreur');
          return EMPTY;
        })
      )
      .subscribe((organismes) => {
        const [premier, ...autres] = organismes;
        this.organisme.set(premier ?? null);
        this.entitesOrganismeDifferent.set(
          autres
            .map((organisme, index) => ({ organisme, idEntite: idsOrdonnes[index + 1] }))
            .filter(({ organisme }) => !this.memeOrganisme(premier, organisme))
            .map(({ idEntite }) => this.libelleEntite(idEntite))
        );
        this.etatOrganisme.set('charge');
      });
  }

  /** Entités sélectionnées dans l'ordre de la liste (et non de la sélection), pour un cadre A stable. */
  public idsEntiteOrdonnes(): number[] {
    const ordonnes = this.entites.filter((entite) => this.idsEntiteSelectionnees.includes(entite.id)).map((entite) => entite.id);
    return ordonnes.length ? ordonnes : [...this.idsEntiteSelectionnees];
  }

  public libelleEntite(idEntite: number): string {
    return this.entites.find((entite) => entite.id === idEntite)?.libelle ?? `Entité ${idEntite}`;
  }

  /** Même organisme déclarant : même n° de déclaration d'activité et même SIRET (espaces et casse ignorés). */
  public memeOrganisme(a: OrganismeFormationModel | null, b: OrganismeFormationModel | null): boolean {
    const normaliser = (valeur: string | null | undefined) => (valeur ?? '').replace(/\s/g, '').toUpperCase();
    return normaliser(a?.numeroActivite) === normaliser(b?.numeroActivite) && normaliser(a?.siret) === normaliser(b?.siret);
  }

  private texteOuNull(valeur: string | null | undefined): string | null {
    return valeur?.trim() || null;
  }

  private formaterAdresse(organisme: OrganismeFormationModel | null): string | null {
    if (!organisme) {
      return null;
    }
    const ville = [organisme.codePostal, organisme.ville].map((partie) => this.texteOuNull(partie)).filter(Boolean).join(' ');
    const parties = [organisme.rue, organisme.complement, ville, organisme.pays].map((partie) => this.texteOuNull(partie)).filter(Boolean);
    return parties.length ? parties.join(', ') : null;
  }

  private buildReturnUrl(): string {
    return this.router.serializeUrl(
      this.router.createUrlTree(['/', AppPath.TDB, StatistiquesPath.BPF])
    );
  }

  private voirDetailInscriptions(titre: string, inscriptions: BpfInscriptionDetail[]): void {
    const detail: BpfDetail = {
      titre: `Détail: ${titre}`,
      lignes: inscriptions.map((inscription) => ({
        principal: this.valeurSessionInscription(inscription),
        secondaires: [this.valeurParticipantInscription(inscription)],
        valeur: `${inscription.heuresFormation} h`,
      })),
    };

    this.modalService.openModal(this.modalType, detail);
  }

  /** Puce participant des popups F/G : mène à l'inscription (le back ne renvoie pas d'identifiant de participant). */
  private valeurParticipantInscription(inscription: BpfInscriptionDetail): BpfDetailValeur {
    return {
      texte: `${inscription.prenomParticipant} ${inscription.nomParticipant}`,
      navigation: { type: 'inscription', id: inscription.idInscription },
    };
  }

  private valeurSessionInscription(inscription: BpfInscriptionDetail): BpfDetailValeur {
    return { texte: inscription.libelleSession, navigation: { type: 'session', id: inscription.idSession } };
  }

  private valeurFormateur(formateur: BpfDetailFormateur): BpfDetailValeur {
    return {
      texte: `${formateur.prenomFormateur} ${formateur.nomFormateur}`,
      navigation: { type: 'formateur', id: formateur.idFormateur },
    };
  }

  private formatMontant(valeur: number): string {
    return new ValeurMonetaireFrPipe().transform(valeur);
  }

  /** Icône représentative du type d'entité ciblée par une valeur de détail, pour la liste de cartes des popups BPF. */
  public iconePourNavigation(navigation: BpfDetailNavigation | undefined): string {
    const icones: Record<BpfDetailNavigationType, string> = {
      session: 'icon-sessions',
      inscription: 'icon-user1',
      formateur: 'icon-graduation',
      'contrat-session': 'icon-money1',
    };
    return navigation ? icones[navigation.type] : 'icon-inbox';
  }

  private calculerDateDebutParDefaut(annee: number): Date {
    return new Date(annee - 1, 0, 1);
  }

  private calculerDateFinParDefaut(annee: number): Date {
    return new Date(annee - 1, 11, 31);
  }

  /** Sous-lignes indentées du cadre C : les lettres a-h (organismes gestionnaires des fonds) et les codes 4-8 (pouvoirs publics pour publics spécifiques). */
  public estSousLigneCerfa(codeCerfa: string): boolean {
    return isNaN(Number(codeCerfa)) || ['4', '5', '6', '7', '8'].includes(codeCerfa);
  }

  /**
   * Regroupe les lignes du cadre C en grandes familles de financeurs pour le donut de la synthèse
   * visuelle. Chaque segment porte une ligne agrégée (mêmes idsFinancements que les lignes CERFA
   * regroupées) pour pouvoir rouvrir la modale de détail existante (voirDetailLigneFinanciere) au clic.
   */
  public repartitionFinanciere(r: BpfResume): SegmentSynthese[] {
    if (!r.bilanFinancier) {
      // Garde-fou défensif : bilanFinancier doit toujours être présent (forkJoin avec GetResumeBpf
      // dans BpfResumeService), mais un résumé ne doit jamais s'afficher à moitié en cas d'anomalie.
      return [];
    }
    const bilanFinancier = r.bilanFinancier;
    const total = bilanFinancier.totalGeneral;
    const lignesParCode = (codesCerfa: string[]): BpfLigneFinanciere[] =>
      bilanFinancier.lignes.filter((ligne) => codesCerfa.includes(ligne.codeCerfa));
    const ligneAgregee = (label: string, lignes: BpfLigneFinanciere[], totalHT: number): BpfLigneFinanciere => ({
      idBpfFinancementType: 0,
      libelle: label,
      codeCerfa: '',
      totalHT,
      financements: lignes.flatMap((ligne) => ligne.financements),
    });

    const entreprises = lignesParCode(['1']);
    const opco = lignesParCode(['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h']);
    const pouvoirsPublics = lignesParCode(['3', '4', '5', '6', '7', '8']);
    const particuliers = lignesParCode(['9']);
    const autres = lignesParCode(['10', '11']);
    const valeurEntreprises = this.sommeLignes(entreprises);
    const valeurPouvoirsPublics = this.sommeLignes(pouvoirsPublics);
    const valeurParticuliers = this.sommeLignes(particuliers);
    const valeurAutres = this.sommeLignes(autres);

    const segmentsBruts: Omit<SegmentSynthese, 'pct'>[] = [
      { label: 'Entreprises', value: valeurEntreprises, couleur: '#2e7cff', ligne: ligneAgregee('Entreprises', entreprises, valeurEntreprises) },
      {
        label: 'Organismes gestionnaires des fonds (OPCO)',
        value: bilanFinancier.totalOpco,
        couleur: '#3acd85',
        ligne: ligneAgregee('Organismes gestionnaires des fonds (OPCO)', opco, bilanFinancier.totalOpco),
      },
      {
        label: 'Pouvoirs publics',
        value: valeurPouvoirsPublics,
        couleur: '#ffb11b',
        ligne: ligneAgregee('Pouvoirs publics', pouvoirsPublics, valeurPouvoirsPublics),
      },
      { label: 'Particuliers', value: valeurParticuliers, couleur: '#ec087e', ligne: ligneAgregee('Particuliers', particuliers, valeurParticuliers) },
      {
        label: 'Autres organismes et autres produits',
        value: valeurAutres,
        couleur: '#a7adb5',
        ligne: ligneAgregee('Autres organismes et autres produits', autres, valeurAutres),
      },
    ];

    return segmentsBruts
      .filter((segment) => segment.value !== 0)
      .map((segment) => ({ ...segment, pct: total ? Math.round((segment.value / total) * 100) : 0 }));
  }

  private sommeLignes(lignes: BpfLigneFinanciere[]): number {
    return lignes.reduce((somme, ligne) => somme + ligne.totalHT, 0);
  }

  /** CSS conic-gradient représentant le donut à partir des pourcentages déjà calculés. */
  public degradeConique(segments: Pick<SegmentSynthese, 'couleur' | 'pct'>[]): string {
    let cumul = 0;
    const arrets = segments.map((segment) => {
      const debut = cumul;
      cumul += segment.pct;
      return `${segment.couleur} ${debut}% ${cumul}%`;
    });
    return arrets.length ? `conic-gradient(${arrets.join(', ')})` : 'none';
  }

  /** Couleur du score de fiabilité, selon les seuils BON/MOYEN. Gris si non calculable. */
  public couleurFiabilite(): string {
    const pct = this.pourcentageFiabilite();
    if (pct == null) {
      return '#75767d';
    }
    if (pct >= this.SEUIL_FIABILITE_BON) {
      return '#1db954';
    }
    if (pct >= this.SEUIL_FIABILITE_MOYEN) {
      return '#ffb11b';
    }
    return '#fb3763';
  }

  /** Anneau de fiabilité (même mécanisme que degradeConique) : rempli jusqu'au pourcentage animé, reste en gris clair. */
  public degradeFiabilite(): string {
    const pct = this.pourcentageFiabilite();
    if (pct == null) {
      return 'none';
    }
    const pctAnime = this.valeurAnimee(pct);
    return this.degradeConique([
      { couleur: this.couleurFiabilite(), pct: pctAnime },
      { couleur: '#edeff5', pct: 100 - pctAnime },
    ]);
  }

  /** Segments de la barre empilée (catégories non vides uniquement, pour ne pas afficher de tronçon vide) ; la légende, elle, affiche toujours les 4 catégories. */
  /** Part d'une catégorie d'audit sur l'ensemble des sessions, pour la légende de la carte Fiabilité (même arrondi que la barre). */
  /**
   * Sessions déclarables = conformes + alertes + bloquantes. Les exclues (hors BPF : session sans
   * date, non validée, désactivée, BPF non activé) n'entrent dans aucun pourcentage de la carte
   * Fiabilité : même base que la jauge, pour que « Conformes x % » y ait partout la même valeur.
   */
  public nombreSessionsDeclarables(audit: BpfAuditGlobal): number {
    const { conforme, alerte, bloquante } = audit.compteurs;
    return conforme + alerte + bloquante;
  }

  public pctCategorieAudit(audit: BpfAuditGlobal, nombre: number): number {
    return this.pourcentage(nombre, this.nombreSessionsDeclarables(audit));
  }

  public segmentsFiabilite(audit: BpfAuditGlobal): SegmentFiabilite[] {
    const { conforme, alerte, bloquante } = audit.compteurs;
    const total = this.nombreSessionsDeclarables(audit);
    if (total === 0) {
      return [];
    }
    return [
      { label: 'Conformes', count: conforme, pct: Math.round((conforme / total) * 100), couleur: '#1db954' },
      { label: 'Bloquantes', count: bloquante, pct: Math.round((bloquante / total) * 100), couleur: '#fb3763' },
      { label: 'Alertes', count: alerte, pct: Math.round((alerte / total) * 100), couleur: '#ffb11b' },
    ].filter((segment) => segment.count > 0);
  }

  /**
   * Top motifs d'anomalie (nombre de SESSIONS distinctes concernées par chaque message, pas le
   * nombre brut d'erreurs : une même session peut cumuler plusieurs erreurs au même message, ex.
   * plusieurs formateurs sans statut BPF sur la même session — elle ne doit compter qu'une fois).
   */
  public topMotifsAnomalie(audit: BpfAuditGlobal, limite: number = 4): { message: string; nombreSessions: number }[] {
    const sessionsParMessage = new Map<string, Set<number>>();
    for (const session of audit.sessions) {
      for (const erreur of session.erreurs) {
        const sessions = sessionsParMessage.get(erreur.message) ?? new Set<number>();
        sessions.add(session.idSession);
        sessionsParMessage.set(erreur.message, sessions);
      }
    }
    return Array.from(sessionsParMessage.entries())
      .map(([message, sessions]) => ({ message, nombreSessions: sessions.size }))
      .sort((a, b) => b.nombreSessions - a.nombreSessions)
      .slice(0, limite);
  }

  public toggleCarte(idCarte: string): void {
    if (this.cartesRepliees.has(idCarte)) {
      this.cartesRepliees.delete(idCarte);
    } else {
      this.cartesRepliees.add(idCarte);
    }
  }

  public estRepliee(idCarte: string): boolean {
    return this.cartesRepliees.has(idCarte);
  }

  /** Copie la valeur brute (sans mise en forme) dans le presse-papiers, pour un collage direct dans la télédéclaration officielle. */
  public copierValeur(valeur: number | string, event: Event): void {
    event.stopPropagation();
    navigator.clipboard.writeText(`${valeur}`).then(
      () => this.afficherPastilleCopie(),
      () => this.notificationToastService.push(new ToastErrorModel('Impossible de copier la valeur'))
    );
  }

  private afficherPastilleCopie(): void {
    if (this.pastilleCopieTimeoutId) {
      clearTimeout(this.pastilleCopieTimeoutId);
    }
    this.pastilleCopieVisible.set(true);
    this.pastilleCopieTimeoutId = setTimeout(() => this.pastilleCopieVisible.set(false), 1200);
  }
}
